import {SupportedLocales} from '#types/locale.js';

export const governanceRulesLanguage = {
  [SupportedLocales.EN]: {
    pageTitle: 'Prioritization & Rules',
    pageDescription: 'Define organization-wide governance rules.',
    createRule: 'Create rule',
    rulesTitle: 'Governance rules',
    rulesDescription:
      'Rules determine whether GrowthLoop alerts, blocks, suppresses, or requires approval.',
    tabs: {rules: 'Rules', approvals: 'Approvals', activity: 'Activity'},
    audience: 'Audience',
    duration: 'Duration',
    priority: 'Priority',
    overlap: 'Overlap',
    attributeRestrictions: 'Attribute Restrictions',
    block: 'Block',
    requestApproval: 'Request approval',
    create: {
      dialogTitle: 'Create rule',
      scopeStep: 'Scope',
      typeStep: 'Rule type',
      configureStep: 'Configure',
      scopeTitle: 'Choose a governed resource',
      scopeDescription: 'What does this rule govern?',
      audienceDescription: 'Set a rule for Audience exports.',
      typeTitle: 'Choose a rule type',
      typeDescription: 'What should this rule control?',
      durationDescription:
        'Limit how long a recurring export can keep running.',
      priorityDescription:
        'Rank audiences so the right one wins when a customer qualifies for several.',
      overlapDescription:
        'Limit how much a new export can overlap an active one.',
      attributeRestrictionsDescription:
        'Control which attributes and attribute combinations can be used to build audiences.',
      attributeRestrictionsNamePlaceholder: 'Audience attribute restrictions',
      restrictedCombination: 'Restricted combination',
      restrictedCombinationHelp: 'About restricted combinations',
      restrictedCombinationDescription:
        'A group matches when all or any of its attributes are used, as selected. Multiple groups are combined with AND logic, so the rule fires only when every group matches.',
      defaultGroupName: 'Group 1',
      groupDefaultName: (number: number) => `Group ${number}`,
      groupNumber: (number: number) => `Group ${number}`,
      addAttributesToGroup: (number: number) =>
        `Add attributes to Group ${number}.`,
      usingDatasetsFrom: 'Using datasets from',
      chooseDatasetGroup: 'Choose Dataset Group',
      searchDatasetGroups: 'Search Dataset Groups',
      noDatasetGroups: 'No Dataset Groups found.',
      chooseDataset: 'Choose Dataset',
      chooseDatasetDescription: 'Choose a Dataset to start adding attributes.',
      searchDatasets: 'Search Datasets',
      loadingDatasets: 'Loading datasets',
      noDatasets: 'No Datasets found.',
      addAttribute: 'Add attribute',
      addAlternative: 'Add alternative',
      searchAttributes: 'Search attributes',
      noAttributes: 'No attributes found.',
      reloadAttributes: 'Could not load attributes. Try another search.',
      recordsMatchOn: 'records match on',
      ofTheseFields: 'of these fields',
      all: 'all',
      any: 'any',
      and: 'AND',
      or: 'OR',
      addGroup: 'Add group',
      deleteGroup: (name: string) => `Delete ${name}`,
      removeAttribute: (name: string) => `Remove ${name}`,
      overlapNamePlaceholder: 'Audience overlap limit',
      minimumAudiencesLabel: 'Minimum audiences',
      minimumAudiencesHelp: 'About minimum audiences',
      minimumAudiencesDescription:
        'Apply this rule only when at least this many audiences have an active export or journey.',
      minimumAudiencesSuffix: 'active audiences',
      maximumOverlapLabel: 'Maximum overlap',
      maximumOverlapHelp: 'About maximum overlap',
      maximumOverlapDescription:
        'Measured against every audience with an active export or journey. Exports at or under the threshold run normally.',
      maximumOverlapSuffix: '% of the new audience',
      decreaseMinimumAudiences: 'Decrease minimum audiences by one',
      increaseMinimumAudiences: 'Increase minimum audiences by one',
      decreaseMaximumOverlap:
        'Decrease maximum overlap by one percentage point',
      increaseMaximumOverlap:
        'Increase maximum overlap by one percentage point',
      configureTitle: 'Configure the rule',
      nameLabel: 'Rule name',
      namePlaceholder: 'Audience duration limit',
      priorityNamePlaceholder: 'Audience priority order',
      durationLabel: 'Maximum duration',
      durationHint:
        'Counted from the first run. Anything scheduled beyond this is caught before it goes live.',
      days: 'days',
      decreaseDuration: 'Decrease maximum duration by one day',
      increaseDuration: 'Increase maximum duration by one day',
      priorityOrderLabel: 'Priority order',
      priorityOrderDescription:
        'Drag to rank audiences, highest priority first.',
      priorityOrderHint: 'Highest first, drag to reorder',
      addAudience: 'Add audience',
      searchAudiences: 'Search audiences',
      noAudiences: 'No audiences found.',
      noAudiencesAdded:
        'No audiences added yet. Add at least two to set an order.',
      profiles: 'profiles',
      loadingDestinationLogos: 'Loading destination logos',
      destinationCountLabel: (count: number) =>
        count === 1 ? 'destination' : 'destinations',
      directExportCountLabel: (count: number) =>
        count === 1 ? 'direct export' : 'direct exports',
      journeyCountLabel: (count: number) =>
        count === 1 ? 'journey' : 'journeys',
      moreTags: (count: number) => `+${count} more`,
      removeAudience: (name: string) => `Remove ${name}`,
      membershipLimitLabel: 'Membership limit',
      membershipLimitHelp: 'About membership limits',
      decreaseMembershipLimit: 'Decrease membership limit by one',
      increaseMembershipLimit: 'Increase membership limit by one',
      membershipLimitSuffix: (count: number) =>
        `${count === 1 ? 'audience' : 'audiences'} per person`,
      membershipDescription: (limit: number, firstAudience: string) =>
        `Once a customer qualifies for ${limit} audience${limit === 1 ? '' : 's'} from this list, they will not be added to any other audience in the list until they stop qualifying for one of them. Priority order decides which audience they land in. ${firstAudience || 'The first audience'} is evaluated first.`,
      thresholdLabel: 'If the threshold is exceeded',
      blockDescription:
        'Stop it outright. The user sees why and can adjust before saving.',
      approvalDescription:
        'Route it to an approver. It waits in Approvals until a decision.',
      back: 'Back',
      next: 'Next',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving',
      saved: 'Governance rule created.',
      saveError: 'The rule could not be saved. Your values are retained.',
      validation: {
        scopeRequired: 'Select Audience.',
        datasetGroupRequired: 'Choose a Dataset Group.',
        datasetRequired: 'Choose a Dataset.',
        attributeRequired: 'Add at least one attribute.',
        typeRequired: 'Select a rule type.',
        nameRequired: 'Enter a rule name.',
        nameTooLong: 'Rule name must be 255 characters or fewer.',
        minimumAudiencesRequired: 'Enter a minimum audience count.',
        minimumAudiencesWhole: 'Minimum audiences must be a whole number.',
        minimumAudiencesRange: 'Minimum audiences must be at least 1.',
        maximumOverlapRequired: 'Enter a maximum overlap.',
        maximumOverlapWhole: 'Maximum overlap must be a whole percentage.',
        maximumOverlapRange: 'Maximum overlap must be between 0 and 100%.',
        durationRequired: 'Enter a maximum duration.',
        durationWhole: 'Maximum duration must be a whole number.',
        durationRange: 'Maximum duration must be at least 1 day.',
        priorityAudienceRequired: 'Add at least one audience.',
        membershipLimitRequired: 'Enter a membership limit.',
        membershipLimitWhole: 'Membership limit must be a whole number.',
        membershipLimitRange: 'Membership limit must be at least 1.',
      },
    },
    table: {
      title: 'Governance rules',
      search: 'Search rule names',
      clearFilters: 'Clear filters',
      emptyTitle: 'Create your first rule',
      emptyDescription:
        'Block or require approval when audiences and journeys fall outside your organization’s policies.',
      noMatches: 'No rules match these filters.',
      noFilterOptions: 'No filter options available.',
      loading: 'Loading governance rules',
      loadError: 'Governance rules could not be loaded.',
      retry: 'Retry',
      columns: {
        id: 'ID',
        rule: 'Rule',
        scope: 'Scope',
        condition: 'Condition',
        action: 'Action',
        status: 'Status',
      },
      values: {
        audience: 'Audience',
        journey: 'Journey',
        block: 'Block',
        requireApproval: 'Require approval',
        configured: 'Configured',
      },
      conditions: {
        attributeRestrictions: (count: number) =>
          `Attribute restrictions across ${count} group${count === 1 ? '' : 's'}`,
        audienceOverlap: (minimumAudiences: number, percentage: number) =>
          `Audience overlap exceeds ${percentage}% with at least ${minimumAudiences} audiences with active exports or journeys`,
        audienceMaximumDuration: (days: number) =>
          `Audience export duration exceeds ${days} days`,
        journeyMaximumDuration: (days: number) =>
          `Journey scheduled run duration exceeds ${days} days`,
        audiencePriority: (audienceCount: number, membershipLimit: number) =>
          `Prioritize ${audienceCount} audiences with a membership limit of ${membershipLimit}`,
      },
    },
  },
  [SupportedLocales.ES]: {
    pageTitle: 'Priorización y reglas',
    pageDescription: 'Define reglas de gobierno para toda la organización.',
    createRule: 'Crear regla',
    rulesTitle: 'Reglas de gobernanza',
    rulesDescription:
      'Las reglas determinan si GrowthLoop alerta, bloquea, suprime o requiere aprobación.',
    tabs: {rules: 'Reglas', approvals: 'Aprobaciones', activity: 'Actividad'},
    audience: 'Audiencia',
    duration: 'Duración',
    priority: 'Prioridad',
    overlap: 'Superposición',
    attributeRestrictions: 'Restricciones de atributos',
    block: 'Bloquear',
    requestApproval: 'Solicitar aprobación',
    create: {
      dialogTitle: 'Crear regla',
      scopeStep: 'Alcance',
      typeStep: 'Tipo de regla',
      configureStep: 'Configurar',
      scopeTitle: 'Elige un recurso gobernado',
      scopeDescription: '¿Qué debe gobernar esta regla?',
      audienceDescription:
        'Establece una regla para las exportaciones de audiencias.',
      typeTitle: 'Elige un tipo de regla',
      typeDescription: '¿Qué debe controlar esta regla?',
      durationDescription:
        'Limita el tiempo que puede ejecutarse una exportación recurrente.',
      priorityDescription:
        'Clasifica las audiencias para que gane la correcta cuando un cliente cumpla varias.',
      overlapDescription:
        'Limita cuánto puede superponerse una nueva exportación con una activa.',
      attributeRestrictionsDescription:
        'Controla qué atributos y combinaciones se pueden usar para crear audiencias.',
      attributeRestrictionsNamePlaceholder:
        'Restricciones de atributos de audiencia',
      restrictedCombination: 'Combinación restringida',
      restrictedCombinationHelp: 'Acerca de las combinaciones restringidas',
      restrictedCombinationDescription:
        'Un grupo coincide cuando se usan todos o cualquiera de sus atributos, según la selección. Los grupos se combinan con lógica Y, por lo que la regla se activa solo cuando todos coinciden.',
      defaultGroupName: 'Grupo 1',
      groupDefaultName: (number: number) => `Grupo ${number}`,
      groupNumber: (number: number) => `Grupo ${number}`,
      addAttributesToGroup: (number: number) =>
        `Añade atributos al grupo ${number}.`,
      usingDatasetsFrom: 'Usando conjuntos de datos de',
      chooseDatasetGroup: 'Elegir grupo de conjuntos de datos',
      searchDatasetGroups: 'Buscar grupos de conjuntos de datos',
      noDatasetGroups: 'No se encontraron grupos de conjuntos de datos.',
      chooseDataset: 'Elegir conjunto de datos',
      chooseDatasetDescription:
        'Elige un conjunto de datos para comenzar a añadir atributos.',
      searchDatasets: 'Buscar conjuntos de datos',
      loadingDatasets: 'Cargando conjuntos de datos',
      noDatasets: 'No se encontraron conjuntos de datos.',
      addAttribute: 'Añadir atributo',
      addAlternative: 'Añadir alternativa',
      searchAttributes: 'Buscar atributos',
      noAttributes: 'No se encontraron atributos.',
      reloadAttributes:
        'No se pudieron cargar los atributos. Intenta otra búsqueda.',
      recordsMatchOn: 'los registros coinciden en',
      ofTheseFields: 'de estos campos',
      all: 'todos',
      any: 'cualquiera',
      and: 'Y',
      or: 'O',
      addGroup: 'Añadir grupo',
      deleteGroup: (name: string) => `Eliminar ${name}`,
      removeAttribute: (name: string) => `Eliminar ${name}`,
      overlapNamePlaceholder: 'Límite de superposición de audiencias',
      minimumAudiencesLabel: 'Mínimo de audiencias',
      minimumAudiencesHelp: 'Acerca del mínimo de audiencias',
      minimumAudiencesDescription:
        'Aplica esta regla solo cuando al menos esta cantidad de audiencias tenga una exportación o un recorrido activo.',
      minimumAudiencesSuffix: 'audiencias activas',
      maximumOverlapLabel: 'Superposición máxima',
      maximumOverlapHelp: 'Acerca de la superposición máxima',
      maximumOverlapDescription:
        'Se mide frente a cada audiencia con una exportación o un recorrido activo. Las exportaciones que no superen el límite se ejecutan normalmente.',
      maximumOverlapSuffix: '% de la nueva audiencia',
      decreaseMinimumAudiences: 'Reducir el mínimo de audiencias en uno',
      increaseMinimumAudiences: 'Aumentar el mínimo de audiencias en uno',
      decreaseMaximumOverlap:
        'Reducir la superposición máxima en un punto porcentual',
      increaseMaximumOverlap:
        'Aumentar la superposición máxima en un punto porcentual',
      configureTitle: 'Configura la regla',
      nameLabel: 'Nombre de la regla',
      namePlaceholder: 'Límite de duración de audiencia',
      priorityNamePlaceholder: 'Orden de prioridad de audiencias',
      durationLabel: 'Duración máxima',
      durationHint:
        'Se cuenta desde la primera ejecución. Todo lo programado después se bloquea antes de publicarse.',
      days: 'días',
      decreaseDuration: 'Reducir la duración máxima en un día',
      increaseDuration: 'Aumentar la duración máxima en un día',
      priorityOrderLabel: 'Orden de prioridad',
      priorityOrderDescription:
        'Arrastra las audiencias de mayor a menor prioridad.',
      priorityOrderHint: 'Primero la más alta, arrastra para reordenar',
      addAudience: 'Añadir audiencia',
      searchAudiences: 'Buscar audiencias',
      noAudiences: 'No se encontraron audiencias.',
      noAudiencesAdded:
        'Aún no se han añadido audiencias. Añade al menos dos para establecer un orden.',
      profiles: 'perfiles',
      loadingDestinationLogos: 'Cargando logotipos de destinos',
      destinationCountLabel: (count: number) =>
        count === 1 ? 'destino' : 'destinos',
      directExportCountLabel: (count: number) =>
        count === 1 ? 'exportación directa' : 'exportaciones directas',
      journeyCountLabel: (count: number) =>
        count === 1 ? 'recorrido' : 'recorridos',
      moreTags: (count: number) => `+${count} más`,
      removeAudience: (name: string) => `Eliminar ${name}`,
      membershipLimitLabel: 'Límite de pertenencia',
      membershipLimitHelp: 'Acerca de los límites de pertenencia',
      decreaseMembershipLimit: 'Reducir el límite de pertenencia en uno',
      increaseMembershipLimit: 'Aumentar el límite de pertenencia en uno',
      membershipLimitSuffix: (count: number) =>
        `${count === 1 ? 'audiencia' : 'audiencias'} por persona`,
      membershipDescription: (limit: number, firstAudience: string) =>
        `Cuando un cliente cumple los requisitos de ${limit} audiencia${limit === 1 ? '' : 's'} de esta lista, no se añadirá a ninguna otra audiencia de la lista hasta que deje de cumplir los requisitos de una de ellas. El orden de prioridad decide en qué audiencia entra. ${firstAudience || 'La primera audiencia'} se evalúa primero.`,
      thresholdLabel: 'Si se supera el límite',
      blockDescription:
        'Detén la operación. El usuario verá el motivo y podrá ajustarla antes de guardar.',
      approvalDescription:
        'Envíala a una persona aprobadora. Esperará en Aprobaciones hasta que se tome una decisión.',
      back: 'Atrás',
      next: 'Siguiente',
      cancel: 'Cancelar',
      save: 'Guardar',
      saving: 'Guardando',
      saved: 'Regla de gobierno creada.',
      saveError:
        'No se pudo guardar la regla. Se conservaron los valores introducidos.',
      validation: {
        scopeRequired: 'Selecciona Audiencia.',
        datasetGroupRequired: 'Elige un grupo de conjuntos de datos.',
        datasetRequired: 'Elige un conjunto de datos.',
        attributeRequired: 'Añade al menos un atributo.',
        typeRequired: 'Selecciona un tipo de regla.',
        nameRequired: 'Introduce un nombre para la regla.',
        nameTooLong: 'El nombre debe tener 255 caracteres o menos.',
        minimumAudiencesRequired: 'Introduce un mínimo de audiencias.',
        minimumAudiencesWhole:
          'El mínimo de audiencias debe ser un número entero.',
        minimumAudiencesRange: 'El mínimo de audiencias debe ser al menos 1.',
        maximumOverlapRequired: 'Introduce una superposición máxima.',
        maximumOverlapWhole:
          'La superposición máxima debe ser un porcentaje entero.',
        maximumOverlapRange:
          'La superposición máxima debe estar entre 0 y 100%.',
        durationRequired: 'Introduce una duración máxima.',
        durationWhole: 'La duración máxima debe ser un número entero.',
        durationRange: 'La duración máxima debe ser al menos 1 día.',
        priorityAudienceRequired: 'Añade al menos una audiencia.',
        membershipLimitRequired: 'Introduce un límite de pertenencia.',
        membershipLimitWhole:
          'El límite de pertenencia debe ser un número entero.',
        membershipLimitRange: 'El límite de pertenencia debe ser al menos 1.',
      },
    },
    table: {
      title: 'Reglas de gobernanza',
      search: 'Buscar nombres de reglas',
      clearFilters: 'Borrar filtros',
      emptyTitle: 'Crea tu primera regla',
      emptyDescription:
        'Bloquea o exige aprobación cuando las audiencias y los recorridos incumplan las políticas de tu organización.',
      noMatches: 'Ninguna regla coincide con estos filtros.',
      noFilterOptions: 'No hay opciones de filtro disponibles.',
      loading: 'Cargando reglas de gobernanza',
      loadError: 'No se pudieron cargar las reglas de gobernanza.',
      retry: 'Reintentar',
      columns: {
        id: 'ID',
        rule: 'Regla',
        scope: 'Ámbito',
        condition: 'Condición',
        action: 'Acción',
        status: 'Estado',
      },
      values: {
        audience: 'Audiencia',
        journey: 'Recorrido',
        block: 'Bloquear',
        requireApproval: 'Requerir aprobación',
        configured: 'Configurada',
      },
      conditions: {
        attributeRestrictions: (count: number) =>
          `Restricciones de atributos en ${count} grupo${count === 1 ? '' : 's'}`,
        audienceOverlap: (minimumAudiences: number, percentage: number) =>
          `La superposición supera el ${percentage}% con al menos ${minimumAudiences} audiencias con exportaciones o recorridos activos`,
        audienceMaximumDuration: (days: number) =>
          `La duración de exportación de la audiencia supera los ${days} días`,
        journeyMaximumDuration: (days: number) =>
          `La duración de la ejecución programada del recorrido supera los ${days} días`,
        audiencePriority: (audienceCount: number, membershipLimit: number) =>
          `Prioriza ${audienceCount} audiencias con un límite de pertenencia de ${membershipLimit}`,
      },
    },
  },
};
